import { watch, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { useMediaQuery } from '@vueuse/core';
import { useViewerModeStore, ViewerMode } from '@/modules/viewer/store/useViewerModeStore';
import { useArtifactsStore } from '@/modules/viewer/store/useArtifactsStore';

const isValidMode = (value: unknown): value is ViewerMode => {
  return typeof value === 'string' && (Object.values(ViewerMode) as string[]).includes(value);
};

/**
 * Keeps `?mode=` in the URL in sync with `useViewerModeStore.currentMode`
 * in both directions:
 *
 * - On mount: reads `?mode=` from the route and applies it to the store
 *   IF the mode is currently available (e.g. not Logs on mobile).
 *   Otherwise leaves both the store and the query param alone so the deep
 *   link can reactivate later when availability flips on (viewport rotates,
 *   artifact loads async).
 * - On availability change: re-applies the query-param mode if it becomes
 *   available and doesn't already match the current mode.
 * - On mode change: replaces the URL's `mode` query param. Video (the default)
 *   strips the param entirely to keep URLs clean — UNLESS the transition was
 *   caused by auto-fallback (previous mode is no longer available), in which
 *   case the query param is preserved so the deep link survives rotation.
 *
 * Share links automatically carry the param because it's in the URL. Uses
 * `router.replace` to avoid history pollution.
 *
 * Call once near the top of a layout component that owns the viewer route.
 */
export const useViewerModeUrlSync = () => {
  const route = useRoute();
  const router = useRouter();
  const viewerModeStore = useViewerModeStore();
  const { currentMode } = storeToRefs(viewerModeStore);
  const { insightsURL } = storeToRefs(useArtifactsStore());
  const isMobile = useMediaQuery('(max-width: 1023px)');

  // Inlined here rather than importing from useViewerAvailableModes so we
  // don't double-register its fallback watcher (which would cause duplicate
  // setMode calls on availability flips). Logic must stay in sync with the
  // `availableModes` computed in that composable.
  const isModeCurrentlyAvailable = (mode: ViewerMode): boolean => {
    if (mode === ViewerMode.VIDEO) return true;
    if (mode === ViewerMode.LOGS) return !!insightsURL.value && !isMobile.value;
    return false;
  };

  onMounted(() => {
    const queryMode = route.query.mode;
    if (!isValidMode(queryMode)) return;
    if (isModeCurrentlyAvailable(queryMode)) {
      viewerModeStore.setMode(queryMode);
    }
  });

  // When availability flips on after mount (e.g. mobile → desktop rotation),
  // reapply the deep-linked mode. The URL param is the user's persistent
  // intent; currentMode is just the rendered state.
  watch([insightsURL, isMobile], () => {
    const queryMode = route.query.mode;
    if (!isValidMode(queryMode)) return;
    if (queryMode === currentMode.value) return;
    if (isModeCurrentlyAvailable(queryMode)) {
      viewerModeStore.setMode(queryMode);
    }
  });

  watch(currentMode, (mode, oldMode) => {
    const currentQueryMode = route.query.mode;
    if (mode === ViewerMode.VIDEO) {
      // Auto-fallback case: we transitioned to VIDEO because the previous
      // mode stopped being available (e.g. viewport shrank while in Logs).
      // Preserve the query param so the deep link reactivates on rotation.
      const isFallback = oldMode !== undefined && oldMode !== ViewerMode.VIDEO && !isModeCurrentlyAvailable(oldMode);
      if (isFallback) return;

      if (currentQueryMode !== undefined) {
        const query = { ...route.query };
        delete query.mode;
        router.replace({ query });
      }
      return;
    }
    if (currentQueryMode !== mode) {
      router.replace({ query: { ...route.query, mode } });
    }
  });
};
